<template>
  <div>
    <l-geo-json
      v-if="geojson && rivers"
      :geojson="geojson"
      :options="{ onEachFeature: onEachFeature, style: optionStyle }"
    ></l-geo-json>
    <l-geo-json
      v-if="riverGeojson"
      :geojson="riverGeojson"
      :options="{ style: lineOptionStyle }"
    ></l-geo-json>
    <v-dialog
      v-model="dialog"
      :width="$vuetify.breakpoint.lgAndUp ? 1200 : 340"
      content-class="elevation-0"
    >
      <v-card style="max-width: 1185px">
        <rain-ensemble-chart-dialog
          v-if="selectedRiver"
          :selected-river="selectedRiver"
          :rivers="rivers"
        ></rain-ensemble-chart-dialog>
        <v-card-actions class="px-3 pb-3 pt-0">
          <v-btn block dark depressed color="warning" @click="closeDialog">
            戻る
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <l-marker
      v-for="(o, index) in warningArea"
      :key="index"
      :lat-lng="o.latlng"
      @click="onClickArea(o.feature)"
    >
      <l-icon :icon-size="[24, 24]">
        <v-btn
          fab
          dark
          height="24"
          width="24"
          :color="getWarningColor(o.river, o.river.real)"
          depressed
          class="blink"
        >
          <v-icon small> mdi-exclamation-thick </v-icon>
        </v-btn>
      </l-icon>
    </l-marker>
  </div>
</template>
<script>
import L from "leaflet";
import _ from "lodash";
import axios from "axios";
import RainEnsembleChartDialog from "@/components/risk-map-view/RainEnsembleChartDialog";
import moment from "moment";
import { UPDATE_BASE_DATE } from "@/store/mutation-types";

const RESPONSE_DATE_FORMAT = "YYYY/MM/DD HH:mm";
const CHART_DATE_FORMAT = "MM/DD HH:mm";

export default {
  name: "RainEnsembleLayer",
  props: ["calendarDate"],
  components: { RainEnsembleChartDialog },
  data() {
    return {
      geojson: null,
      rivers: [],
      selectedRiver: null,
      dialog: false,
      warningArea: [],
      riverGeojson: null,
    };
  },
  async created() {
    this.rivers = await this.loadChartData();
    this.geojson = await this.loadGeojson();
  },
  computed: {
    baseDate() {
      return this.$store.state.baseDate;
    },
  },
  watch: {
    async calendarDate() {
      this.rivers = await this.updateChartData(this.rivers);
      this.warningArea = this.getWarningArea();
    },
  },
  methods: {
    async loadGeojson() {
      const url =
        "https://s3.ap-northeast-1.amazonaws.com/ctie.realtime.basin.ensemble/watershed.geojson";
      const response = await axios.get(url);
      return response.data;
    },
    async onEachFeature(feature, layer) {
      layer.on("click", async (e) => {
        this.rivers = await this.updateChartData(this.rivers);

        const id = e.target.feature.properties.id;
        this.selectedRiver = _.find(this.rivers, (river) => {
          return id === river.id;
        });

        this.$nextTick(() => {
          this.dialog = true;
        });
      });
      const name = feature.properties["name"];
      const popup = `<div class="re-popup"><div class="popup-title">流域名</div><div class="popup-name">${name}</div></div>`;
      layer.bindPopup(popup, { closeButton: false, offset: L.point(0, -20) });
      layer.on("mouseover", function () {
        layer.openPopup();
      });
      layer.on("mouseout", function () {
        layer.closePopup();
      });
    },
    async onClickArea(feature) {
      this.rivers = await this.updateChartData(this.rivers);

      const id = feature.properties.id;
      this.selectedRiver = _.find(this.rivers, (river) => {
        return id === river.id;
      });

      this.$nextTick(() => {
        this.dialog = true;
      });
    },
    closeDialog() {
      this.dialog = false;
      this.selectedRiver = null;
    },
    async loadChartData() {
      let rivers;
      rivers = await this.loadRivers();
      rivers = await this.updateChartData(rivers);
      this.warningArea = this.getWarningArea();

      this.riverGeojson = await this.loadRiverClass1();

      return rivers;
    },
    async updateChartData(rivers) {
      const data = await this.loadData();
      const reals = data.reals;
      const forecasts = data.forecasts;

      rivers.forEach((river) => {
        const id = parseInt(river.id);
        let realTime = river.durationTime;
        if (realTime > 48) {
          realTime = 48;
        }
        river.realTime = realTime;
        river.realBaseDate = data.realBaseDate
          ? moment
              .utc(data.realBaseDate, RESPONSE_DATE_FORMAT)
              .local()
              .format(CHART_DATE_FORMAT)
          : "欠測";
        if (reals) {
          const real = _.find(reals, { id: id });
          river.real = real["h" + realTime];
        }

        let forecastTime = river.durationTime;
        if (forecastTime > 39) {
          forecastTime = 39;
        }
        river.forecastTime = forecastTime;
        river.forecastBaseDate = data.forecastBaseDate
          ? moment
              .utc(data.forecastBaseDate, RESPONSE_DATE_FORMAT)
              .local()
              .format(CHART_DATE_FORMAT)
          : "欠測";
        if (forecasts) {
          const forecast = _.find(forecasts, { id: id });
          river.forecast = forecast["max" + forecastTime + "hr"];
        }
      });

      return rivers;
    },
    async loadRivers() {
      const url =
        "https://s3.ap-northeast-1.amazonaws.com/ctie.realtime.basin.ensemble/rivers.json";
      const response = await axios.get(url);

      return response.data;
    },
    async loadRiverClass1() {
      const url =
        "https://s3.ap-northeast-1.amazonaws.com/ctie.realtime.basin.ensemble/rivClass1.geojson";
      const response = await axios.get(url);

      return response.data;
    },
    async loadData() {
      const url = `https://data.riskma.net/bosai/rain-emsemble`;
      const date = this.calendarDate
        ? moment(this.calendarDate, "YYYY/MM/DD HH:mm")
        : moment();
      const response = await axios.get(url, {
        params: { date: date.utc().format("YYYY/MM/DD HH:mm") },
      });

      const baseDate = moment
        .utc(response.data.baseDate, RESPONSE_DATE_FORMAT)
        .local();
      this.$store.commit(UPDATE_BASE_DATE, { baseDate });

      return response.data;
    },
    optionStyle(feature) {
      const id = feature.properties.id;
      const river = _.find(this.rivers, (river) => {
        return id === river.id;
      });

      if (river) {
        const forecastMax = _.max(river.forecast);
        const level = river.real > forecastMax ? river.real : forecastMax;
        const color = this.getWarningColor(river, level);
        return {
          color: color,
          weight: 1,
          backgroundColor: color,
        };
      } else {
        return {
          color: "#3388ff",
          backgroundColor: "#3388ff",
        };
      }
    },
    lineOptionStyle() {
      return {
        color: "#3388ff",
        backgroundColor: "#3388ff",
      };
    },
    getWarningColor(river, level) {
      if (river.LMAX && river.LMAX < level) {
        return "#000000";
      } else if (river.L200 && river.L200 < level) {
        return "#880088";
      } else if (river.L150 && river.L150 < level) {
        return "#f027ff";
      } else if (river.L100 && river.L100 < level) {
        return "#EA3323";
      } else if (river.L50 && river.L50 < level) {
        return "#FF8038";
      } else if (river.L30 && river.L30 < level) {
        return "#FEFF54";
      } else if (river.L10 && river.L10 < level) {
        return "#238100";
      } else {
        return "#3388ff";
      }
    },
    getWarningArea() {
      const areas = [];
      _.forEach(this.rivers, (river) => {
        const isWarning = ((river, level) => {
          if (river.LMAX && river.LMAX < level) {
            return true;
          } else if (river.L200 && river.L200 < level) {
            return true;
          } else if (river.L150 && river.L150 < level) {
            return true;
          } else if (river.L100 && river.L100 < level) {
            return true;
          } else if (river.L50 && river.L50 < level) {
            return true;
          } else if (river.L30 && river.L30 < level) {
            return true;
          } else if (river.L10 && river.L10 < level) {
            return true;
          } else {
            return false;
          }
        })(river, river.real);

        if (isWarning) {
          const area = _.find(this.geojson, (o) => {
            return o.properties.id === river.id;
          });

          let centerLatlng;
          if (Array.isArray(area.geometry.coordinates[0][0][0])) {
            centerLatlng = this.calculateCenter(
              area.geometry.coordinates[area.geometry.coordinates.length - 1][0]
            );
          } else {
            centerLatlng = this.calculateCenter(area.geometry.coordinates[0]);
          }

          areas.push({
            latlng: centerLatlng,
            feature: area,
            river: river,
          });
        }
      });

      return areas;
    },
    calculateCenter(coords) {
      if (coords.length === 0) {
        return null; // 좌표가 없을 경우 null 반환
      }

      let totalX = 0;
      let totalY = 0;

      for (const coord of coords) {
        totalX += coord[1];
        totalY += coord[0];
      }

      const centerX = totalX / coords.length;
      const centerY = totalY / coords.length;

      return { lat: centerX, lng: centerY };
    },
  },
};
</script>

<style>
.leaflet-popup-content-wrapper,
.leaflet-popup-content {
  border-radius: 5px !important;
}
.leaflet-popup-content {
  margin: 10px !important;
}
.re-popup {
  min-width: 70px;
  text-align: center;
  border-radius: 10px;
}
.popup-title {
  font-size: 12px;
  color: #28549b;
  margin-bottom: 5px;
  font-weight: bold;
}
.popup-name {
  font-size: 14px;
}
.blink {
  animation: blinker 1s linear infinite;
}

@keyframes blinker {
  50% {
    opacity: 0;
  }
}
</style>
